import { jsx as _jsx } from "react/jsx-runtime";
import * as React from "react";
import { cva } from "class-variance-authority";
import { cn } from "./lib/utils.js";
const dateTypes = ["date", "datetime-local", "month", "time", "week"];
const inputWrapperVariants = cva(
// Base wrapper styles
[
    "ui:relative ui:w-full ui:items-center ui:rounded-md",
    "ui:text-fg ui:text-base ui:sm:text-sm/6",
    // Border
    "ui:border-[1.5px]",
    // Focus ring
    "ui:focus-within:border-border-focus ui:focus-within:ring-3 ui:focus-within:ring-accent-2-surface",
    // Disabled
    "ui:has-disabled:opacity-50 ui:has-disabled:cursor-not-allowed",
], {
    variants: {
        variant: {
            default: [
                // Background
                "ui:bg-surface-2",
                // Border (non-invalid)
                "ui:border-border ui:not-focus-within:hover:border-border-strong",
                // Invalid state — :user-invalid rather than :invalid, plus
                // aria-invalid for form-library validation (react-hook-form,
                // TanStack Form). :invalid matches an empty `required` field from
                // the first paint, so a form of four required inputs opened as four
                // red boxes before anyone had typed a character. :user-invalid holds
                // until the field has actually been used and left wrong, which is
                // the only moment the red is telling the reader anything.
                "ui:has-[input:user-invalid]:border-error ui:has-[[aria-invalid=true]]:border-error",
            ],
            filled: [
                // Background
                "ui:bg-surface-0 ui:focus-within:bg-surface-1",
                // Border (non-invalid)
                "ui:border-transparent",
                // Invalid state — see the note on the first variant: :user-invalid,
                // so an untouched required field is not already red.
                "ui:has-[input:user-invalid]:border-error ui:has-[[aria-invalid=true]]:border-error",
            ],
            raised: [
                // Background — most elevated surface so the field reads as a target
                "ui:bg-surface-4",
                // Clip the inner input to the wrapper's rounded edge so the fill
                // never bleeds past the border-radius (esp. inside an InputGroup).
                "ui:overflow-hidden",
                // Border (non-invalid)
                "ui:border-border ui:not-focus-within:hover:border-border-strong",
                // Invalid state — see the note on the first variant: :user-invalid,
                // so an untouched required field is not already red.
                "ui:has-[input:user-invalid]:border-error ui:has-[[aria-invalid=true]]:border-error",
            ],
        },
    },
    defaultVariants: {
        variant: "default",
    },
});
const inputInnerVariants = cva(
// Base input styles
[
    "ui:block ui:w-full ui:min-w-0 ui:bg-transparent ui:py-[calc(--spacing(2)-1px)]",
    // slashed-zero + lining-nums keep digits legible (0 vs O) in Raleway
    "ui:text-fg ui:lining-nums ui:slashed-zero ui:placeholder:text-fg-faint",
    "ui:focus:outline-hidden",
    // Disabled / read-only
    "ui:disabled:cursor-not-allowed",
    "ui:read-only:text-fg-muted",
], {
    variants: {
        variant: {
            default: [
                // Layout
                "ui:px-[calc(--spacing(3.5)-1px)] ui:sm:px-[calc(--spacing(3)-1px)]",
                // Typography
                "ui:text-base/6 ui:sm:text-sm/6",
            ],
            filled: [
                // Layout
                "ui:px-[calc(--spacing(3.5)-1px)] ui:sm:px-[calc(--spacing(3)-1px)]",
                // Typography
                "ui:text-base/6 ui:sm:text-sm/6",
            ],
            raised: [
                // Layout
                "ui:px-[calc(--spacing(3.5)-1px)] ui:sm:px-[calc(--spacing(3)-1px)]",
                // Typography
                "ui:text-base/6 ui:sm:text-sm/6",
            ],
        },
    },
    defaultVariants: {
        variant: "default",
    },
});
const Input = React.forwardRef(({ className = "", type, value, variant, ...props }, ref) => {
    // Convert null values to empty string
    const sanitizedValue = value === null ? "" : value;
    return (_jsx("div", { className: cn(inputWrapperVariants({ variant }), className), children: _jsx("input", { type: type, value: sanitizedValue, className: cn(dateTypes.includes(type) && [
                "ui:[&::-webkit-datetime-edit-fields-wrapper]:p-0",
                "ui:[&::-webkit-date-and-time-value]:min-h-[1.5em]",
                "ui:[&::-webkit-datetime-edit]:inline-flex",
                "ui:[&::-webkit-datetime-edit]:p-0",
                "ui:[&::-webkit-datetime-edit-year-field]:p-0",
                "ui:[&::-webkit-datetime-edit-month-field]:p-0",
                "ui:[&::-webkit-datetime-edit-day-field]:p-0",
                "ui:[&::-webkit-datetime-edit-hour-field]:p-0",
                "ui:[&::-webkit-datetime-edit-minute-field]:p-0",
                "ui:[&::-webkit-datetime-edit-second-field]:p-0",
                "ui:[&::-webkit-datetime-edit-millisecond-field]:p-0",
                "ui:[&::-webkit-datetime-edit-meridiem-field]:p-0",
            ], inputInnerVariants({ variant })), ref: ref, ...props }) }));
});
Input.displayName = "Input";
export { Input, inputWrapperVariants, inputInnerVariants };
